import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { ArrowLeft, Loader2, CheckCircle, Lock, Eye, EyeOff } from "lucide-react";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
        <div className="w-full max-w-[380px] bg-white border border-[#E5E7EB] p-6 sm:p-8 text-center">
          <h1 className="font-display text-[20px] tracking-[-0.03em] text-[#0A0A0A] mb-2">Invalid Link</h1>
          <p className="text-[14px] text-[#6B7280] mb-4">This reset link is invalid or has expired.</p>
          <Link to="/forgot-password" className="text-[14px] font-semibold text-[#1A3DFF] hover:underline">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-[380px]">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-[13px] text-[#6B7280] hover:text-[#0A0A0A] mb-6">
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to login
        </Link>

        <div className="bg-white border border-[#E5E7EB] p-6 sm:p-8">
          {success ? (
            <div className="text-center" data-testid="reset-password-success">
              <div className="w-12 h-12 bg-[#ECFDF5] border border-[#10B981] mx-auto mb-4 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-[#10B981]" />
              </div>
              <h1 className="font-display text-[20px] tracking-[-0.03em] text-[#0A0A0A] mb-2">Password reset!</h1>
              <p className="text-[14px] text-[#6B7280]">Redirecting you to login...</p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <div className="w-10 h-10 bg-[#F3F4F6] border border-[#E5E7EB] flex items-center justify-center mb-4">
                  <Lock className="w-5 h-5 text-[#6B7280]" />
                </div>
                <h1 className="font-display text-[20px] tracking-[-0.03em] text-[#0A0A0A] mb-1">Set new password</h1>
                <p className="text-[14px] text-[#6B7280]">Enter your new password below.</p>
              </div>

              {error && (
                <div className="bg-[#FEF2F2] border border-[#FECACA] p-3 mb-4" data-testid="reset-password-error">
                  <p className="text-[13px] text-[#DC2626]">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <label className="block text-[12px] font-semibold text-[#0A0A0A] uppercase tracking-[0.05em] mb-1.5">New Password</label>
                <div className="relative mb-3">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    required
                    className="w-full border border-[#E5E7EB] px-3 py-2.5 text-[14px] text-[#0A0A0A] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#0A0A0A] transition-colors pr-10"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                <label className="block text-[12px] font-semibold text-[#0A0A0A] uppercase tracking-[0.05em] mb-1.5">Confirm Password</label>
                <input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  required
                  className="w-full border border-[#E5E7EB] px-3 py-2.5 text-[14px] text-[#0A0A0A] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#0A0A0A] transition-colors"
                  data-testid="reset-password-confirm"
                />

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#0A0A0A] text-white font-semibold text-[14px] py-2.5 mt-4 hover:bg-[#1A1A1A] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="reset-password-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset Password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
